"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

export default function ContactCTA() {
  const { ref: leftRef, isVisible: leftVisible } = useRevealOnScroll<HTMLDivElement>({ threshold: 0.15 });
  const { ref: rightRef, isVisible: rightVisible } = useRevealOnScroll<HTMLDivElement>({ threshold: 0.15 });

  const stats = [
    { value: "15+", label: "years of service", align: "left" },
    { value: "500+", label: "trees maintained", align: "right" },
    { value: "100%", label: "client satisfaction", align: "left" },
  ];

  return (
    <section data-section-id="2138"
      id="contactcta"
      className="relative overflow-hidden bg-background text-foreground py-24 md:py-44 px-5 sm:px-8 lg:px-18"
    >
      {}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute font-serif select-none leading-none"
        style={{
          fontSize: "clamp(12rem, 22vw, 22rem)",
          fontWeight: 200,
          color: "hsl(30 15% 25% / 0.05)",
          top: "8%",
          left: "2%",
          letterSpacing: "-0.05em",
        }}
      >
        04
      </div>

      <div className="relative mx-auto max-w-[1440px] grid grid-cols-12 gap-6 md:gap-8 items-start">
        {}
        <div
          ref={leftRef}
          className="col-span-12 lg:col-span-5 order-2 lg:order-1 flex flex-col"
          style={{
            opacity: leftVisible ? 1 : 0,
            transform: leftVisible ? "translateY(0)" : "translateY(24px)",
            transition: "opacity 640ms cubic-bezier(0.22, 1, 0.36, 1), transform 640ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {}
          <div className="flex items-center gap-3 mb-8">
            <span
              aria-hidden="true"
              className="inline-block h-px w-6 bg-primary"
            />
            <span
              className="font-default uppercase"
              style={{
                fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
                fontWeight: 500,
                letterSpacing: "0.18em",
                lineHeight: 1,
              }}
            >
              § N° 04 — GET IN TOUCH
            </span>
          </div>

          {}
          <h2
            className="font-default mb-8"
            style={{
              fontSize: "clamp(2.25rem, 4.2vw, 4.25rem)",
              fontWeight: 400,
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
          >
            Professional care for your{" "}
            <span
              className="italic font-default"
            >
              urban
            </span>{" "}
            canopy.
          </h2>

          {}
          <p
            className="mb-12 max-w-xl font-default"
            style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
              fontWeight: 400,
              lineHeight: 1.6,
              letterSpacing: "-0.005em",
            }}
          >
            Arbor Pro provides expert tree health management for Fort Worth homeowners. From precision pruning to emergency removals, we treat every tree with the respect and technical expertise it deserves.
          </p>

          {}
          <div className="flex flex-col gap-7 mb-12">
            {stats.map((stat, i) => (
              <div
                key={i}
                data-index={i}
                className={`flex items-center gap-5 ${stat.align === "right" ? "flex-row-reverse text-right" : ""}`}
              >
                <div className="relative flex items-center flex-1 min-w-0">
                  <span
                    className="h-px flex-1 bg-border"
                  />
                  <span
                    aria-hidden="true"
                    className="block h-1.5 w-1.5 rounded-lg ml-0 bg-accent"
                  />
                </div>
                <div className="flex items-baseline gap-3 shrink-0">
                  <span
                    className="font-default"
                    style={{
                      fontSize: "clamp(1.75rem, 2.5vw, 2.5rem)",
                      fontWeight: 400,
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                    }}
                  >
                    {stat.value}
                  </span>
                  <span
                    className="font-default uppercase"
                    style={{
                      fontSize: "0.6875rem",
                      letterSpacing: "0.18em",
                    }}
                  >
                    / {stat.label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/GetQuotePage">
              <Button
                className="rounded-lg px-8 py-6 h-auto font-default uppercase transition-all duration-[220ms] ease-out hover:-translate-y-0.5 bg-primary text-primary-foreground"
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  borderRadius: "999px",
                }}
              >
                Request a Free Quote
              </Button>
            </Link>
            <Link to="/ResidentialTreeCarePage">
              <Button
                variant="outline"
                className="rounded-lg px-8 py-6 h-auto font-default uppercase bg-transparent transition-colors duration-[220ms] ease-out border-primary text-primary"
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  borderRadius: "999px",
                  borderWidth: "1px",
                }}
              >
                View Our Services
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div
          ref={rightRef}
          className="col-span-12 lg:col-span-7 order-1 lg:order-2"
          style={{
            opacity: rightVisible ? 1 : 0,
            transform: rightVisible ? "translateY(0)" : "translateY(24px)",
            transition: "opacity 640ms cubic-bezier(0.22, 1, 0.36, 1) 80ms, transform 640ms cubic-bezier(0.22, 1, 0.36, 1) 80ms",
          }}
        >
          <div className="relative w-full" style={{ aspectRatio: "16 / 10" }}>
            {}
            <span
              aria-hidden="true"
              className="absolute top-0 left-0 z-10 border-t border-l border-muted"
              style={{
                width: "8px",
                height: "8px",
              }}
            />
            <span
              aria-hidden="true"
              className="absolute top-0 right-0 z-10 border-t border-r border-muted"
              style={{
                width: "8px",
                height: "8px",
              }}
            />
            <span
              aria-hidden="true"
              className="absolute bottom-0 left-0 z-10 border-b border-l border-muted"
              style={{
                width: "8px",
                height: "8px",
              }}
            />
            <span
              aria-hidden="true"
              className="absolute bottom-0 right-0 z-10 border-b border-r border-accent"
              style={{
                width: "8px",
                height: "8px",
              }}
            />

            <img
              src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arbor_pro_tree_service_fort_worth.png"
              alt="Professional tree care team performing maintenance in a Fort Worth residential neighborhood"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover rounded-lg"
            />

            {}
            <div
              className="absolute bottom-4 left-4 z-10 font-default uppercase"
              style={{
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                lineHeight: 1.2,
                color: "white",
                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
              }}
            >
              Project N° 014 — Fort Worth Residential / 2024
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}